import React, { useState, useEffect } from "react";
import { Copy, Download } from "lucide-react";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { Avatar, AvatarImage } from "./ui/Avatar";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";

export default function QRCard() {
  const [copied, setCopied] = useState(false);
  const [profile, setProfile] = useState({});
  const [id, setId] = useState("");
  const navigate = useNavigate();
  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      `https://user.tringbox.com/launch/partner/${id}`,
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    const canvas = document.querySelector("canvas");
    if (canvas) {
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = url;
      link.download = "qr-code.png";
      link.click();
    }
  };

  useEffect(() => {
    fetchWithAuth(API_ENDPOINTS.PARTNER, {
      method: "GET",
    })
      .then((response) => response.json())
      .then((data) => {
        setProfile(data);
        setId(data.id);
      })
      .catch((error) => {
        console.error("Error fetching profile:", error);
      });
  }, []);

  const notes = ["♪", "♫", "♩", "♬", "♭", "♯", "🎵", "🎶"];

  return (
    <div className="w-full flex flex-col items-center relative min-h-screen">
      {/* Musical Notes Animation */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {Array.from({ length: 40 }).map((_, i) => (
          <motion.div
            key={`note-${i}`}
            className="absolute text-[#72288D]/20 text-3xl md:text-4xl"
            initial={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -50, 0],
              x: [0, Math.random() * 30 - 15, 0],
              opacity: [0.3, 0.7, 0.3],
              rotate: [0, Math.random() * 360, 0],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut",
            }}
          >
            {notes[i % notes.length]}
          </motion.div>
        ))}
      </div>

      {/* Header Strip - Hidden on mobile */}
      <div className="w-full fixed top-0 right-0 md:pl-80 z-20 hidden md:block">
        <motion.div
          className="w-full bg-white shadow-md border-b-2 border-purple-200 p-6 flex items-center justify-between gap-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar className="w-16 h-16  border-purple-300">
                <AvatarImage
                  src={profile?.avatar}
                  alt={profile?.display_name}
                />
              </Avatar>
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-purple-200/50"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                }}
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                Welcome,{" "}
                <span className="text-purple-600">
                  {profile?.display_name || "User"}
                </span>
              </h1>
              <p className="text-sm text-gray-500">
                Share your QR code to connect
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-4xl mx-auto px-4 flex flex-col items-center relative mt-20 md:mt-56 z-10">
        {/* QR Code Section with Revolving Rectangle */}
        <div className="relative">
          {/* Revolving Rectangle Animation */}
          <motion.div
            className="absolute md:-inset-6 -inset-1 rounded-3xl border-4 border-[#72288D]/30"
            animate={{
              rotate: [0, 360],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "linear",
            }}
          />

          {/* QR Code Container */}
          <motion.div
            className="relative bg-white md:p-8 p-3 rounded-3xl shadow-xl"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-64 h-64 bg-white rounded-2xl flex items-center justify-center">
              <QRCodeCanvas
                value={`https://user.tringbox.com/launch/partner/${id}`}
                size={256}
              />
            </div>
          </motion.div>
        </div>

        {/* Text below QR */}
        <div className="w-full flex flex-col items-center justify-center mt-10 md:mt-16 mb-1 text-center">
          <h2 className="text-xl font-medium text-[#2D0A4A] mb-2">
            Scan to Connect
          </h2>
          <motion.h3
            className="text-3xl md:text-4xl font-bold text-[#72288D] md:mb-8 mb-2"
            initial={{ scale: 1 }}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            {profile?.display_name || "User"}
          </motion.h3>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-2  gap-3 w-full max-w-lg mt-6">
          <motion.button
            onClick={handleCopyLink}
            className="flex items-center justify-center gap-2 bg-white border-2 border-[#72288D] text-[#72288D] px-6 py-3 rounded-xl font-medium hover:bg-[#72288D]/5 shadow-sm"
            whileTap={{ scale: 0.98 }}
          >
            <Copy size={20} />
            <span>{copied ? "Copied!" : "Copy"}</span>
          </motion.button>
          <motion.button
            onClick={handleDownloadQR}
            className="flex items-center justify-center gap-2 bg-white border-2 border-[#72288D] text-[#72288D] px-6 py-3 rounded-xl font-medium hover:bg-[#72288D]/5 shadow-sm"
            whileTap={{ scale: 0.98 }}
          >
            <Download size={20} />
            <span>Download</span>
          </motion.button>
        </div>
      </div>
    </div>
  );
}
